// COP Copilot — the runtime⇄working-set bridge (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// THE CONFINEMENT SEAM between the lifted agent-runtime and the COP's PDP-visible working set.
// It does two things:
//   1. buildManifest(workingSet)  — projects the copilot's capability set into the McpManifest the
//      model is constrained to (table.query / table.manifest / maps.* / request_evac), enriched
//      with the VISIBLE-ONLY data catalog (workingSet.manifest() — Glomar; values/zones that exist
//      only on a DENIED object are never enumerated).
//   2. makeExecuteToolCall(workingSet) — the SOLE tool-execution surface. Every table.query runs
//      via copilotGeoQuery.executeQuery over workingSet.getTable(...) — the host-filtered rows ONLY
//      — and returns a PREVIEW of those already-cleared rows + the classification roll-up. A denied
//      object is absent from the working set, so it can never be counted, ordered, or returned
//      (Glomar by construction). maps.* verbs are ACK-only intent records (no engine write — the
//      capability's reroute owns the live host-paint in Wave B); request_evac is host-gated and
//      NEVER dispatched from the model here (it returns an ack the host human-gate would clear).
//
// THE MODEL NEVER SEES: a raw CanonicalEnvelope, a sealed/decrypted payload, or a denied-object
// existence/count. It sees ONLY the preview rows this bridge returns (already cleared) + the
// rollup marking. This is the exact discipline takbot's context.ts/describeResult enforces,
// preserved end-to-end.
//
// Holds NO React, never writes the engine (no ctx.engine access at all), imports ONLY types +
// the pure executor from sibling copilot modules.

import { executeQuery, QueryError, type TableQuery } from "../copilotGeoQuery.ts";
import type { CopilotWorkingSet } from "../copilotTables.ts";
import type { ExecuteToolCall } from "./runtime/driver.ts";
import type { McpManifest, TakbotManifest, ToolCall, ToolResult } from "./runtime/types.ts";
import { CAP } from "./runtime/types.ts";

/** Compute the high-water-mark classification across a set of cleared rows (the roll-up the
 *  preview carries). UNCLASSIFIED < CONFIDENTIAL < SECRET < TOP_SECRET; an unknown level sorts
 *  just above UNCLASSIFIED (fail-toward-more-sensitive). */
const LEVEL_RANK: Record<string, number> = {
  UNCLASSIFIED: 0,
  CONFIDENTIAL: 2,
  SECRET: 3,
  "TOP SECRET": 4,
  TOP_SECRET: 4,
  TOPSECRET: 4,
};
function rollupOf(rows: Array<Record<string, unknown>>): string | undefined {
  let best: string | undefined;
  let bestRank = -1;
  for (const r of rows) {
    const c = r.classification;
    if (typeof c !== "string" || c.trim().length === 0) continue;
    const key = c.trim().toUpperCase();
    const rank = LEVEL_RANK[key] ?? 1;
    if (rank > bestRank) {
      bestRank = rank;
      best = c;
    }
  }
  return best;
}

/** Build the MCP manifest the model is constrained to — the copilot's capability surface + the
 *  VISIBLE-ONLY data catalog. The tool input schemas are intentionally compact (the model also
 *  gets the Q1 notes from prompt.ts buildToolList). */
export function buildManifest(workingSet: CopilotWorkingSet): McpManifest {
  const tool = (
    name: string,
    description: string,
    risk: "read" | "reversible" | "irreversible",
    properties: Record<string, unknown>,
    required: string[] = [],
    humanGated = false,
  ): McpManifest["tools"][number] => ({
    name,
    description,
    inputSchema: { type: "object", properties, required, additionalProperties: true },
    _scop: { risk, actors: ["ai-agent", "human", "npe"], humanGated },
  });

  const manifest: TakbotManifest = {
    $schema: "scop.mcp.tools/1",
    tools: [
      tool(CAP.TABLE_MANIFEST, "List the queryable tables, columns, and the values you are cleared to see.", "read", {}),
      tool(
        CAP.TABLE_QUERY,
        "Run a declarative Q1 query over the visible picture: {from,where?,select?,order_by?,limit?,output?}. Filter (scalar + geo bbox/radius/within/intersects). Returns a preview of the matching cleared rows + the classification roll-up.",
        "read",
        {
          from: { type: "string", description: "Table: 'entities' | 'zones'." },
          where: { type: "array" },
          select: { type: "array" },
          order_by: { type: "array" },
          limit: { type: "integer" },
          output: { type: "object" },
        },
        ["from"],
      ),
      tool(CAP.MAPS_FILTER_LAYER, "Show only the entities matching a predicate on the map layer.", "reversible", {
        layer_id: { type: "string" },
        predicate: { type: "object" },
      }),
      tool(CAP.MAPS_HIGHLIGHT_ROWS, "Highlight the rows of a prior query result_ref on the map.", "reversible", {
        result_ref: { type: "string" },
        id_column: { type: "string" },
        label: { type: "string" },
      }),
      tool(CAP.MAPS_CLEAR_HIGHLIGHTS, "Clear all highlight rings from the map.", "reversible", {}),
      tool(CAP.MAPS_SET_VIEWPORT, "Move the map camera (bbox [w,s,e,n] or center [lng,lat] + zoom).", "reversible", {
        bbox: { type: "array" },
        center: { type: "array" },
        zoom: { type: "number" },
      }),
      tool(
        CAP.MAPS_REQUEST_EVAC,
        "LIFE-SAFETY: request evacuation at the user's HOST-SUPPLIED position. Never include a location — only an optional priority/note. Host-gated (human-only).",
        "irreversible",
        { priority: { type: "string" }, note: { type: "string" } },
        [],
        true,
      ),
    ],
  };
  // The VISIBLE-only catalog (Glomar): values/zones derived from the cleared working set only.
  const cat = workingSet.manifest();
  const zonesCatalog = Array.isArray(cat.zones_catalog)
    ? (cat.zones_catalog as { id: string; name: string; zone_type: string | null }[])
    : [];
  const entityValues =
    cat.entity_values && typeof cat.entity_values === "object"
      ? (cat.entity_values as Record<string, string[]>)
      : {};
  manifest.catalog = {
    zones_catalog: zonesCatalog,
    entity_values: entityValues,
    ...(typeof cat.hint === "string" ? { hint: cat.hint } : {}),
  };
  return manifest;
}

/** The result-ref store for a single turn — maps a query's result_ref to the rows it returned so a
 *  later maps.highlight_rows can chain off it WITHOUT re-deriving (and without the model ever
 *  seeing more than the preview). Scoped per turn by the caller. */
export class ResultRefStore {
  private seq = 0;
  private readonly byRef = new Map<string, Array<Record<string, unknown>>>();
  mint(rows: Array<Record<string, unknown>>): string {
    this.seq += 1;
    const ref = `result_${this.seq}`;
    this.byRef.set(ref, rows);
    return ref;
  }
  get(ref: string): Array<Record<string, unknown>> | undefined {
    return this.byRef.get(ref);
  }
}

/** Build the SOLE tool-execution function the driver uses, bound to the confined working set.
 *  This is the only place a tool actually runs — over the PDP-visible rows ONLY. */
export function makeExecuteToolCall(
  workingSet: CopilotWorkingSet,
  refs: ResultRefStore = new ResultRefStore(),
): ExecuteToolCall {
  const ok = (call: ToolCall, result: unknown): ToolResult => ({ call_id: call.call_id, ok: true, result });
  const fail = (call: ToolCall, code: string, message: string): ToolResult => ({
    call_id: call.call_id,
    ok: false,
    error: { code, message },
  });

  return (call: ToolCall): ToolResult => {
    const cap = call.capability;
    try {
      if (cap === CAP.TABLE_MANIFEST) {
        // The VISIBLE-only manifest (Glomar). No raw envelopes — just the catalog.
        return ok(call, { manifest: workingSet.manifest() });
      }

      if (cap === CAP.TABLE_QUERY) {
        const query = call.args as TableQuery;
        const fromName = typeof query?.from === "string" ? query.from : query?.from?.table;
        if (typeof fromName !== "string" || !workingSet.has(fromName)) {
          // Glomar-safe: denied-because-invisible == genuinely-unknown.
          return fail(call, "unknown_table", `unknown table "${String(fromName)}"`);
        }
        const rows = workingSet.getTable(fromName);
        if (!rows) return fail(call, "unknown_table", `unknown table "${fromName}"`);
        const r = executeQuery(query, rows, workingSet.zonesPolygonMap());
        const previewCount = query.output?.preview_rows ?? 20;
        const preview = r.rows.slice(0, previewCount) as Array<Record<string, unknown>>;
        const ref = refs.mint(r.rows as Array<Record<string, unknown>>);
        const rollup = rollupOf(r.rows as Array<Record<string, unknown>>);
        // The model sees ONLY this preview of already-cleared rows + the roll-up. A denied object
        // is absent from `rows`, so it is absent here — no count, no marker (Glomar).
        return ok(call, {
          result_ref: ref,
          preview: {
            columns: r.columns,
            rows: preview,
            total_rows: r.rows.length,
            truncated: r.rows.length > preview.length,
          },
          rows_returned: r.rows.length,
          ...(rollup ? { rollup } : {}),
          provenance: {
            source_table: fromName,
            rows_returned: r.stats.rows_returned,
            coverage_warning:
              "Rows reflect pre-positioned map candidates under your clearance + need-to-know, not a canonical census.",
          },
        });
      }

      if (cap === CAP.MAPS_HIGHLIGHT_ROWS) {
        const a = (call.args ?? {}) as { result_ref?: string };
        const set = a.result_ref ? refs.get(a.result_ref) : undefined;
        return ok(call, { ack: true, highlighted: set ? set.length : 0, result_ref: a.result_ref ?? null });
      }

      if (cap === CAP.MAPS_FILTER_LAYER || cap === CAP.MAPS_CLEAR_HIGHLIGHTS || cap === CAP.MAPS_SET_VIEWPORT) {
        // Reversible map intent — ACK only at the runtime seam (the capability's host-mediated
        // reroute owns the live host-paint). No engine write here.
        return ok(call, { ack: true });
      }

      if (cap === CAP.MAPS_REQUEST_EVAC) {
        // Host-gated, human-only. The runtime acknowledges the INTENT; the host human-gate is the
        // dispatch boundary (no auto-dispatch from the model). NO coordinates accepted/returned.
        return ok(call, { ack: true, gated: true, note: "evac intent surfaced; host human-gate required to dispatch" });
      }

      return fail(call, "unknown_capability", `unknown capability ${cap}`);
    } catch (e) {
      if (e instanceof QueryError) return fail(call, e.code, e.message);
      return fail(call, "internal_error", e instanceof Error ? e.message : String(e));
    }
  };
}
